import React, { JSX, useEffect, useState } from "react";
import { Modal, ModalAction } from "components/modal/modal";
import {
    Paragraph,
    ParagraphSize,
    ParagraphStyle,
} from "components/typography/paragraph/paragraph";
import { t } from "utilities/localization/t";
import { FormTextArea } from "components/form/form-textarea/form-textarea";
import { InputTypes } from "components/form/enumerations/input-types";
import { EnrollmentRecord } from "models/view-models/enrollments/enrollment-record";
import { ToastManager } from "utilities/toast/toast-manager";
import { ButtonStyle } from "components/buttons/button/button";
import { FormRadioButton } from "components/form/form-radio-button/form-radio-button";
import "./withdraw-enrollment-modal.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface WithdrawEnrollmentModalProps {
    enrollmentToEdit: EnrollmentRecord;
    onSave?: (enrollment: EnrollmentRecord, sendEmailToLearner: boolean) => void;
    isOpen: boolean;
    onClose: (close: boolean) => void;
    enrollmentName: string;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    captureComment?: boolean;
    showSendEmailToLearner?: boolean;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const WithdrawEnrollmentModal: React.FC<WithdrawEnrollmentModalProps> = ({
    //  actions,
    isOpen,
    onClose,
    enrollmentName,
    enrollmentToEdit,
    onSave,
    setOpen,
    captureComment = true,
    showSendEmailToLearner = false
}: WithdrawEnrollmentModalProps): JSX.Element => {
    const CSS_CLASS_NAME: string = "event-activation-registration-section";
    const [withdrawnComment, setWithdrawnComment] = React.useState<string>(enrollmentToEdit?.withdrawnComment ?? "");
    const [dirty, setDirty] = React.useState<boolean>(false);
    const [disabled, setDisabled] = React.useState<boolean>(false);
    const [enrollment, setEnrollment] = useState<EnrollmentRecord>(enrollmentToEdit);
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [sendWithdrawnEmail, setSendWithdrawnEmail] = useState<boolean|undefined>(false);

    const handleUpdateWithdrawnComment = (comment: string): void => {
        setWithdrawnComment(comment);
        setDirty(true);
    }

    useEffect(() => {
        setEnrollment(enrollmentToEdit);
        setWithdrawnComment(enrollmentToEdit?.withdrawnComment ?? "");
        setSendWithdrawnEmail(false);
        setDirty(false);
    }, [enrollmentToEdit]);

    useEffect(() => {

        if (!captureComment && !showSendEmailToLearner) {
            setDisabled(false);
            return;
        }

        if (!showSendEmailToLearner) {
            setDisabled(withdrawnComment.trim() === "");
            return;
        }

        if (withdrawnComment.trim() === "" || sendWithdrawnEmail === undefined) {
            setDisabled(true);
        } else {
            setDisabled(false);
        }
    }, [withdrawnComment, sendWithdrawnEmail, captureComment, showSendEmailToLearner]);

    const handleSave = async (): Promise<void> => {
        setDirty(false);
        
        const updatedEnrollment = enrollment.with({
            withdrawnComment: withdrawnComment,
        });
        
        setEnrollment(updatedEnrollment);
        if (await onSave?.(updatedEnrollment, sendWithdrawnEmail ?? false)) {
            onModalClose();
        } else {
            ToastManager.error("failedToSaveWithdrawnEnrollment");
        }
    };

    const modalActionArray: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => onModalCancel(),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("saveChanges"),
            disabled: disabled, 
            onClick: () => handleSave(),
            style: ButtonStyle.Primary,
        },
    ];

    const onModalCancel = (): void => {
        if (dirty) {
            setShowConfirmationModal(true);
            return;
        }
        onModalClose();
    };

    const onModalClose = (): void => {
        setSendWithdrawnEmail(false);
        setWithdrawnComment(enrollment.withdrawnComment ?? "");
        setDirty(false);
        setOpen(false);
    };

    const getTitle = (): string => {
        return enrollmentToEdit?.isWithdrawn() ? t("viewWithdrawnComment") :t("withdrawALearner");
    }
    const confirmationActionArray: ModalAction[] = [
            {
                buttonText: t("cancel"),
                onClick: () => setShowConfirmationModal(false),
                style: ButtonStyle.Secondary,
            },
            {
                buttonText: t("confirm"),
                onClick: () => {
                    setWithdrawnComment(enrollment.withdrawnComment ?? "");
                    setSendWithdrawnEmail(false);
                    setOpen(false);
                    setShowConfirmationModal(false);
                },
                style: ButtonStyle.Destructive,
            },
        ];

    return (
        <Modal
            actions={enrollmentToEdit?.isWithdrawn() ? [] : modalActionArray}
            isOpen={isOpen}
            modalStyle={""}
            onModalClose={onModalCancel}
            title={getTitle()}>
            {enrollmentToEdit?.isWithdrawn() && (
                <>
                <p>{t("withdrawnBy")}: {enrollmentToEdit?.withdrawnBy?.displayName()?.trim() ? enrollmentToEdit.withdrawnBy.displayName() : enrollmentToEdit?.withdrawnById}</p>
                <p>{enrollmentToEdit?.withdrawnComment}</p>
                </>
            )}
            {!enrollmentToEdit?.isWithdrawn() && (
                <>
                <div className={`${CSS_CLASS_NAME}__message`}>
                    <Paragraph size={ParagraphSize.Large}>
                        {t("areYouSureYouWantToWithdrawThisLearner")}
                    </Paragraph>
                    <br />
                    <Paragraph style={ParagraphStyle.Light}>
                        {t("youAreWithdrawingEnrollmentName", { enrollmentName: enrollmentName })} <br />
                        {t("thisWillImmediatelyRemoveThemFromThisTraining")}
                    </Paragraph>
                </div>
                <br />
                {captureComment && (
                    <FormTextArea
                        ariaLabelledBy={t("withdrawnComment")}
                        formFieldName="withdrawnComment"
                        id="withdrawnComment"
                        label={t("withdrawnComment")}
                        onChange={(event) => handleUpdateWithdrawnComment(event.target.value)}
                        placeholder={t("withdrawnComment")}
                        required={true}
                        rows={4}
                        value={withdrawnComment}
                        type={InputTypes.Text}
                    />
                )}
                
                {showSendEmailToLearner && (
                <div className={`${CSS_CLASS_NAME}__toggle-container`}>
                    <label>{t("sendWithdrawnEmail")}:</label>
                    <FormRadioButton
                        ariaLabelledBy="radio-button-no"
                        checked={sendWithdrawnEmail == null ? false : !sendWithdrawnEmail}
                        disabled={true}
                        label={t("no")}
                        formFieldName={t("no")}
                        id="radio-button-no"
                        tabIndex={-1}
                        onChange={() => setSendWithdrawnEmail(false)}
                    />
                    <FormRadioButton
                        ariaLabelledBy="radio-button-yes"
                        checked={sendWithdrawnEmail == null ? false : sendWithdrawnEmail}
                        disabled={true}
                        label={t("yes")}
                        formFieldName={t("yes")}
                        id="radio-button-yes"
                        tabIndex={-1}
                        onChange={() => setSendWithdrawnEmail(true)}
                    />
                </div>
                )}
                <Modal
                    isOpen={showConfirmationModal}
                    onModalClose={() => {}}
                    actions={confirmationActionArray}
                    modalStyle={"-inverted"}>
                    {t("youHaveUnsavedChanges")}
                    <br></br> {t("areYouSureYouWantToExit")}
                </Modal>
                </>
            )}
        </Modal>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { WithdrawEnrollmentModal };

// #endregion Exports
